<template>
  <div :ref="listId" class="vue-view vue-view--team-actions-overview">
    <div class="vue-view__filters">
      <div class="filters__form">
        <div class="filters__filter">
          <label>{{ Drupal.t("Search for a team action", {}, {'context': 'VueJs Team Actions search'}) }}</label>

          <div class="filter-keyword-wrapper">
            <FormInput
              @change="onKeywordChange"
              :value="model.filter.keyword"
              :placeholder="Drupal.t('Search by keyword or location', {}, {'context': 'VueJs Team Actions search'})"
              class="form-control"
              :required="false"
              clearable
            />
            <button type="button" class="btn btn-primary" @click="applyFilters()">{{ Drupal.t("Search", {}, {'context': 'VueJs Team Actions search'}) }}</button>
          </div>
        </div>
      </div>
    </div>

    <Spinner v-if="loading"/>

    <div v-if="error">
      <div class="alert alert-danger">{{ error }}</div>
    </div>

    <div class="vue-view__content" :ref="listId + '__content'">
      <div v-if="loaded && !loading && !error">
        <div class="vue-view__results">
          <div v-if="!count">
            <div class="alert alert-info">{{ Drupal.t("No team actions were found.", {}, {'context': 'VueJs Team Actions search'}) }}</div>
          </div>

          <div v-else>
            <div class="row">
              <template v-for="row in results" :key="row">
                <slot :row="row">
                  {{ row }}
                </slot>
              </template>
            </div>

            <Pagination
              :count="count"
              :result-count="results.length"
              :totalPages="pagination.totalPages"
              :perPage="pagination.perPage"
              :currentPage="pagination.currentPage"
              @pagechanged="pagerClickCallback"
              v-if="pagination.totalPages > 1"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {parse} from "qs"
import {mapActions} from "vuex"
import {DrupalJsonApiParams} from "drupal-jsonapi-params"

import Pagination from "@/components/Pagination/PaginationComponent"
import Spinner from "@/components/General/SpinnerComponent"
import FormInput from "@/components/Form/FormInput.vue";

export default {
  name: "TeamActionsOverview",
  components: {
    FormInput,
    Spinner,
    Pagination
  },
  props: {
    actionId: null,
    perPage: null,
  },
  data() {
    // Parse our model from the visible query string.
    const queryString = this.$window.location.search.split("?")[1] || ""
    const model = parse(queryString)

    return {
      listId: "team_actions",
      path: this.$window.location.pathname,
      model: {
        filter: model.filter || {},
        page: parseInt(model.page) || 1,
        sort: model.sort || "field_date_end",
      },
      config: {
        pagination: {
          perPage: 10,
        },
        isMobile: false
      },
      resource: null,
      Drupal: this.$drupal,
      loading: false,
      loaded: false,
      needsRefresh: false,
      error: false,
    }
  },
  computed: {
    /**
     * Return the count of keys in our filter object.
     *
     * @returns {boolean}
     */
    hasActiveFilters() {
      let hasValues = false
      for (const filterKey in this.model.filter) {
        if (filterKey in this.model.filter) {
          if (filterKey === 'keyword') continue

          const filterValues = this.model.filter[filterKey];

          if ((Array.isArray(filterValues) || this.$helpers.isString(filterValues)) && filterValues.length) {
            hasValues = true
          }
        }
      }

      return hasValues
    },

    /**
     * The pagination.
     *
     * @returns {object}
     */
    pagination() {
      const pagination = {}

      pagination.perPage = this.perPage ?? this.config.pagination.perPage
      pagination.totalPages = this.count ? Math.ceil(this.count / pagination.perPage) : 0
      pagination.currentPage = this.model.page
      pagination.pageStartNum = ((pagination.currentPage - 1) * pagination.perPage) + 1
      pagination.pageEndNum = ((pagination.currentPage - 1) * pagination.perPage) + this.results.length

      return pagination
    },

    /**
     * The total item count.
     *
     * @type {int}
     */
    count() {
      return parseInt(((this.resource || {}).meta || {}).count) || 0
    },

    /**
     * Our resource results.
     *
     * @type {object[]}
     */
    results() {
      return (this.resource || {}).data || []
    },
  },
  methods: {
    onWindowResize(event) {
      this.config.isMobile = this.$helpers.isMobile()
    },

    /**
     * Scroll to the top of the application.
     */
    scrollToTop() {
      const element = this.$refs[this.listId + '__content']
      const top = element.offsetTop

      window.scrollTo(0, top);
    },

    /**
     * Builds the query for the JSON:API request.
     *
     * @param {object} settings - The merged module and component settings object.
     *
     * @return {DrupalJsonApiParams}
     */
    getQuery(settings = {}) {
      const query = new DrupalJsonApiParams()

      // Pagination.
      const pageParam = {}
      pageParam.limit = this.pagination.perPage

      if (this.model.page > 1) {
        pageParam.offset = (this.model.page - 1) * this.pagination.perPage
      }

      query.addCustomParam({
        page: pageParam
      })

      // Filters.
      if (this.model.filter.keyword) {
        query.addFilter("fulltext", this.model.filter.keyword)
      }
      // Only show actions that haven't finished yet.
      const now = new Date().toISOString().split('.');
      query.addFilter('field_date_end', now[0], '>=')

      // Exposed sorts.
      if (this.model.sort) {
        query.addSort(this.model.sort)
      }

      return query.getQueryObject()
    },

    /**
     * Wrapper for fetchData.
     *
     * @returns {Promise<*>}
     */
    async $fetch() {
      // Fetch resource.
      if (this.fetchData) {
        try {
          await this.fetchData.call(this, {})
        } catch (err) {
          // Build error details.
          const title = (err.response || {}).statusText || ((((err.response || {}).data || {}).errors || [])[0] || {}).title
          const summary = (err.response || {}).status
            ? [(err.response || {}).status, title].filter((s) => s).join(': ')
            : err.message

          console.log(title, summary)

          return false
        }
      }
    },

    /**
     * Filters update event handler.
     */
    onFiltersUpdate() {
      this.model.page = 1
    },

    /**
     * Filters update event handler.
     */
    onKeywordChange(value) {
      if (value.length) {
        this.model.filter.keyword = value
      } else {
        delete this.model.filter.keyword
      }
    },

    /**
     * Vuex actions.
     */
    ...mapActions("team_actions", [
      "getResults"
    ]),

    /**
     * Set the publicly visible query parameters.
     *
     * @param {object} query
     */
    setQueryParams(query) {
      if (query.sort && query.sort === "field_date_start") {
        delete query.sort
      }

      this.$router.push({path: this.path, query: query})
    },

    /**
     * Build the visible query parameters object.
     *
     * @returns {{filter, page, sort}}
     */
    buildQueryParams() {
      return this.model
    },

    /**
     * Build the visible query string from our model.
     */
    setModelToQueryString() {
      this.setQueryParams(this.buildQueryParams())
    },

    /**
     * Filter on the selected model values.
     */
    async applyFilters() {
      // Reset paging.
      this.onFiltersUpdate()

      this.setModelToQueryString()

      await this.$fetch()
    },

    /**
     * Build the visible query string from our model.
     */
    async resetFilters() {
      this.model = {
        filter: {},
        page: 1,
        sort: "-changed",
      }

      if (this.config.isMobile) {
        this.setModelToQueryString()

        await this.$fetch()
      }
    },

    /**
     * Handle pager clicks.
     *
     * @param {int} pageNum
     */
    async pagerClickCallback(pageNum) {
      this.model.page = pageNum

      this.setModelToQueryString()

      await this.$fetch()

      this.scrollToTop()
    },

    /**
     * Fetch JSON:API results.
     *
     * @param {Object} settings
     */
    async fetchData(settings) {
      if (this.actionId) {
        const query = this.getQuery(settings)

        this.loading = true
        this.getResults({
          actionId: this.actionId,
          query: query,
        })
          .then(resource => {
            this.resource = resource

            this.loading = false
            this.loaded = true
          }).catch((error) => {
          this.error = Drupal.t("Could not load the team actions.", {}, {'context': 'VueJs Team Actions search'})
          this.loaded = false
        }).finally(() => {
          this.loading = false
        })
      }
    },
  },
  watch: {
    "model.filter": {
      deep: true,
      async handler(to, from) {
        if (this.config.isMobile) {
          return
        }

        // Reset paging.
        this.onFiltersUpdate()

        this.setModelToQueryString()

        await this.$fetch()
      },
    },

    async "model.sort"(to, from) {
      if (to !== from) {
        this.setModelToQueryString()

        await this.$fetch()
      }
    },
  },
  mounted() {
    this.$fetch()
  },
  created() {
    window.addEventListener("resize", this.onWindowResize)
    this.onWindowResize()
  },
  unmounted() {
    window.removeEventListener("resize", this.onWindowResize)
  }
}
</script>
